import { LICENSE_LIMIT } from '../config/constants.js';

/**
 * データモデル定義
 * Firestoreのコレクション名とデータ構造を定義
 */

// コレクション名
export const COLLECTIONS = {
  CLINICS: 'clinics',
  USERS: 'users',
  LICENSE_LIST: 'license-list',
  ANNOUNCEMENTS: 'announcements'
};

// ロール定義
export const USER_ROLES = {
  ADMIN: 'admin',      // システム管理者
  DOCTOR: 'doctor',    // 医師
  STAFF: 'staff'       // スタッフ
};

/**
 * クリニックデータモデル
 * @typedef {Object} Clinic
 * @property {string} id - クリニックID
 * @property {string} name - クリニック名
 * @property {string|null} logoUrl - ロゴURL（Storageパス）
 * @property {string} address - 住所
 * @property {string} phone - 電話番号
 * @property {string} email - メールアドレス
 * @property {string} contactPerson - 担当者名
 * @property {string} paymentMethod - 支払方法（原則: 'credit'）
 * @property {boolean} hasPaymentInfo - 支払情報の有無
 * @property {number|null} userLimit - ユーザー数上限（null = 無制限）
 * @property {Object} licenses - ライセンス管理オブジェクト（license01〜license30）
 * @property {Object} settings - クリニック設定
 * @property {Timestamp} createdAt - 作成日時
 * @property {Timestamp} updatedAt - 更新日時
 */

/**
 * ユーザーデータモデル
 * @typedef {Object} User
 * @property {string} uid - ユーザーUID (Firebase Auth)
 * @property {string} userId - ユーザーID（同一クリニック内で一意）
 * @property {string} email - メールアドレス
 * @property {string} displayName - 表示名
 * @property {string} clinicId - 所属クリニックID
 * @property {string} role - ユーザーロール (admin/doctor/staff)
 * @property {boolean} isActive - アカウント有効状態
 * @property {Timestamp} createdAt - 作成日時
 * @property {Timestamp} updatedAt - 更新日時
 */

/**
 * Build empty license object (license01..license{limit}).
 * Keep in sync with server/helpers/licenseHelper.js createEmptyLicenses.
 * @returns {Object} license object
 */
export function createEmptyLicenses(limit = LICENSE_LIMIT) {
  const licenses = {};
  for (let i = 1; i <= limit; i++) {
    const licenseKey = `license${String(i).padStart(2, '0')}`;
    licenses[licenseKey] = '';
  }
  return licenses;
}

/**
 * クリニックデータの初期値を生成
 * @param {Object} clinicData - クリニックの基本情報
 * @returns {Object} クリニックデータ
 */
export function createClinicData(clinicData) {
  return {
    name: clinicData.name || '',
    logoUrl: clinicData.logoUrl || null,
    postalCode: clinicData.postalCode || '',
    address: clinicData.address || '',
    phone: clinicData.phone || '',
    email: clinicData.email || '',
    contactPerson: clinicData.contactPerson || '',
    paymentMethod: clinicData.paymentMethod || 'credit',
    hasPaymentInfo: clinicData.hasPaymentInfo || false,
    hasPaymentInfoTest: clinicData.hasPaymentInfoTest === true,
    hasPaymentInfoLive: clinicData.hasPaymentInfoLive === true,
    userLimit: clinicData.userLimit !== undefined ? clinicData.userLimit : null,
    isDemo: clinicData.isDemo === true,
    licenses: createEmptyLicenses(clinicData.licenseLimit),
    settings: clinicData.settings || {
      palettes: {},
      thresholdsOverrides: {},
      featureFlags: {
        enableAIDiagnosis: true,
        enableMediaUpload: true
      }
    }
  };
}

/**
 * ユーザーデータの初期値を生成
 * @param {Object} userData - ユーザーの基本情報
 * @returns {Object} ユーザーデータ
 */
export function createUserData(userData) {
  return {
    userId: userData.userId || '',
    email: userData.email || '',
    displayName: userData.displayName || '',
    clinicId: userData.clinicId || '',
    role: userData.role || USER_ROLES.STAFF,
    isActive: userData.isActive !== undefined ? userData.isActive : true
  };
}

/**
 * ライセンス一覧を取得（空でないもののみ）
 * @param {Object} licenses - ライセンスオブジェクト
 * @returns {Array<{key: string, name: string}>} - ライセンス一覧
 */
export function getActiveLicenses(licenses, limit = LICENSE_LIMIT) {
  const activeLicenses = [];
  for (let i = 1; i <= limit; i++) {
    const licenseKey = `license${String(i).padStart(2, '0')}`;
    if (licenses[licenseKey] && licenses[licenseKey] !== '') {
      activeLicenses.push({
        key: licenseKey,
        name: licenses[licenseKey]
      });
    }
  }
  return activeLicenses;
}

